import { faCheck, faPencil, faTrash } from "@fortawesome/free-solid-svg-icons";
import CreateButton from "components/Modals/CreateButton.tsx";
import { Dialog, DialogBody, DialogTitle } from "components/Theme/dialog.tsx";
import dayjs, { type Dayjs } from "dayjs";
import { Form, Formik } from "formik";
import { isError } from "lodash-es";
import React from "react";
import SuccessBanner from "../../../components/Banner/SuccessBanner.tsx";
import { ErrorBanner } from "../../../components/Error/ErrorBanner.tsx";
import SingleDatePicker from "../../../components/Forms/SingleDatePicker.tsx";
import CancelButton from "../../../components/Modals/CancelButton.tsx";
import { Heading } from "../../../components/Theme/heading.tsx";
import DisplayIfUserType from "../../../components/utils/DisplayIfUserType/DisplayIfUserType.tsx";
import { useBulkUpdateIssue } from "../../../hooks/updateEntity.tsx";
import {
  type GetIssuesIssueGetQueryParams,
  type IssueWithAssetRegister,
  type UpdateIssueWithIssueId,
  issueCategoriesEnum,
} from "../../../kubb";
import IssueSelectComponentWrapper from "../IssueList/IssueSelectComponentWrapper.tsx";

interface BulkManualIssueEditModalProps {
  initialFilters: GetIssuesIssueGetQueryParams;
  refresh?: () => void;
}

interface CloseFormValues {
  end_date_time: Dayjs;
}

export default function BulkManualIssueEditModal(
  props: BulkManualIssueEditModalProps,
): React.ReactElement {
  const [edit, setEditing] = React.useState(false);
  const [success, setSuccess] = React.useState<
    boolean | "cancelled" | "closed"
  >(false);
  const [lastRequestDate, setLastRequestDate] = React.useState(new Date());
  const [error, setError] = React.useState<Error | undefined>(undefined);
  const [updatedNumber, setUpdatedNumber] = React.useState<number>(0);
  const update = useBulkUpdateIssue();

  const submit = async (values: UpdateIssueWithIssueId[]): Promise<any> => {
    setSuccess(false);
    setError(undefined);
    setUpdatedNumber(0);
    try {
      await update.update({ issues: values });
      setUpdatedNumber(values.length);
      setLastRequestDate(new Date());
    } catch (e) {
      if (update.error) {
        setError(update.error);
      } else {
        if (isError(e)) {
          setError(e);
        } else {
          setError(Error("Unknown Error"));
        }
      }
      throw e;
    }
    props.refresh?.();
  };

  const cancelSelectedIssues = async () => {
    await submit(
      selected.map((v) => {
        return {
          ...v,
          cancelled: true,
        };
      }),
    );
    setSuccess("cancelled");
  };

  const closeSelectedIssues = async (values: CloseFormValues) => {
    await submit(
      selected.map((v) => {
        return {
          ...v,
          end_date_time: values.end_date_time.endOf("day").toISOString(),
        };
      }),
    );
    setSuccess("closed");
  };

  const [selected, setSelected] = React.useState<IssueWithAssetRegister[]>([]);

  const issueText = `${selected.length} issue${selected.length !== 1 ? "s" : ""}`;
  const updatedIssueText = `${updatedNumber} issue${updatedNumber !== 1 ? "s were" : " was"}`;

  const close = () => {
    setSuccess(false);
    setUpdatedNumber(0);
    setError(undefined);
    setEditing(false);
  };

  return (
    <DisplayIfUserType userTypes={"editor"}>
      <Dialog onClose={close} size={"7xl"} open={edit}>
        <DialogTitle>Bulk Update Manual Issues</DialogTitle>
        <DialogBody>
          <Heading level={4}>Select Issues</Heading>
          <IssueSelectComponentWrapper
            initialValues={{ ...props.initialFilters, max_items: 1000 }}
            setValues={setSelected}
            lastUpdateDate={lastRequestDate}
            issueCategories={[issueCategoriesEnum["MANUAL.NOT_ADOPTED"]]}
          />
          <Heading level={4}>Cancel Issues</Heading>
          <p>
            Remove these issues completely. This is the equivalent of deleting
            the issues. All reports and jobs will revert as if these issues
            never exists.
          </p>
          <div className={"flex justify-self-end gap-2"}>
            <CancelButton
              onClick={() => cancelSelectedIssues()}
              labelText={`Cancel ${issueText}`}
              labelIcon={faTrash}
              disabled={selected.length === 0}
              loading={update.loading}
            />
          </div>
          <Heading level={4}>Close Issues</Heading>
          <p>
            Close this selected issues. This marks the issues as having been
            resolved on the date that you select.
          </p>
          <Formik
            initialValues={{ end_date_time: dayjs() }}
            onSubmit={closeSelectedIssues}
          >
            {() => (
              <Form>
                <SingleDatePicker
                  dateName={"end_date_time"}
                  label={{ label: "Issue End Date", size: "md" }}
                />
                <div className={"flex justify-self-end gap-2"}>
                  <CancelButton
                    type={"submit"}
                    labelText={`Close ${issueText}`}
                    labelIcon={faCheck}
                    disabled={selected.length === 0}
                    loading={update.loading}
                  />
                </div>
              </Form>
            )}
          </Formik>
        </DialogBody>
        {success && <SuccessBanner title={`${updatedIssueText} ${success}.`} />}
        <ErrorBanner error={error} />
      </Dialog>
      <CreateButton
        onClick={() => setEditing(true)}
        labelText={"Bulk Update Manual Issues"}
        labelIcon={faPencil}
        color={"zinc"}
      />
    </DisplayIfUserType>
  );
}
